import React, { useState, useMemo } from 'react';
import { X, Search, Image, Film, Check, Filter } from 'lucide-react';
import { useApp } from '../../../contexts/AppContext';

/**
 * CreativePickerModal - Modal for selecting creatives from Asset Library
 */
const CreativePickerModal = ({
    isOpen,
    onClose,
    onSelect,
    selectedId = null
}) => {
    const { creatives, selectedBrand } = useApp();
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all', 'image', 'video'
    const [selectedCreative, setSelectedCreative] = useState(null);

    // Filter creatives by brand
    const brandCreatives = useMemo(() => {
        if (!selectedBrand) return [];
        return creatives.filter(c => c.brandId === selectedBrand.id);
    }, [creatives, selectedBrand]);

    // Apply search and type filters
    const filteredCreatives = useMemo(() => {
        let filtered = [...brandCreatives];

        // Search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(c =>
                c.title?.toLowerCase().includes(query) ||
                c.description?.toLowerCase().includes(query)
            );
        }

        // Type filter
        if (filterType === 'image') {
            filtered = filtered.filter(c => !c.isVideo && !c.videoUrl);
        } else if (filterType === 'video') {
            filtered = filtered.filter(c => c.isVideo || c.videoUrl);
        }

        return filtered;
    }, [brandCreatives, searchQuery, filterType]);

    const handleSelect = (creative) => {
        setSelectedCreative(creative);
    };

    const handleConfirm = () => {
        if (selectedCreative) {
            onSelect(selectedCreative);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="modal-backdrop"
            onClick={onClose}
            style={{ zIndex: 10001 }}
        >
            <div
                className="modal"
                onClick={(e) => e.stopPropagation()}
                style={{
                    maxWidth: '900px',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Header */}
                <div className="modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--color-primary-light)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Image size={20} style={{ color: 'var(--color-primary)' }} />
                        </div>
                        <div>
                            <h2 className="modal-title">Select Creative</h2>
                            <p style={{
                                margin: 0,
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                Choose from your Asset Library
                            </p>
                        </div>
                    </div>
                    <button className="btn btn-icon btn-ghost" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Search and filters */}
                <div style={{
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)',
                    display: 'flex',
                    gap: 'var(--space-3)'
                }}>
                    {/* Search */}
                    <div style={{ flex: 1, position: 'relative' }}>
                        <Search
                            size={16}
                            style={{
                                position: 'absolute',
                                left: 'var(--space-3)',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'var(--color-text-tertiary)'
                            }}
                        />
                        <input
                            type="text"
                            className="input-field"
                            placeholder="Search creatives..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ paddingLeft: 'var(--space-8)' }}
                        />
                    </div>

                    {/* Type filter */}
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-1)',
                        background: 'var(--color-bg-secondary)',
                        padding: 'var(--space-1)',
                        borderRadius: 'var(--radius-md)'
                    }}>
                        {[
                            { value: 'all', label: 'All', icon: Filter },
                            { value: 'image', label: 'Images', icon: Image },
                            { value: 'video', label: 'Videos', icon: Film }
                        ].map(filter => (
                            <button
                                key={filter.value}
                                type="button"
                                onClick={() => setFilterType(filter.value)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    padding: 'var(--space-2) var(--space-3)',
                                    background: filterType === filter.value
                                        ? 'var(--color-bg-primary)'
                                        : 'transparent',
                                    border: 'none',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: filterType === filter.value ? 600 : 400,
                                    color: filterType === filter.value
                                        ? 'var(--color-text-primary)'
                                        : 'var(--color-text-secondary)',
                                    boxShadow: filterType === filter.value
                                        ? 'var(--shadow-sm)'
                                        : 'none'
                                }}
                            >
                                <filter.icon size={14} />
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Creative grid */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: 'var(--space-4)'
                }}>
                    {filteredCreatives.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 'var(--space-8)',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            <Image size={48} style={{ marginBottom: 'var(--space-3)', opacity: 0.5 }} />
                            <p style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>
                                No creatives found
                            </p>
                            <p style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--font-size-sm)' }}>
                                {searchQuery
                                    ? 'Try a different search term'
                                    : 'Generate some creatives first!'}
                            </p>
                        </div>
                    ) : (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: 'var(--space-3)'
                        }}>
                            {filteredCreatives.map(creative => {
                                const isSelected = selectedCreative?.id === creative.id || selectedId === creative.id;
                                const thumbnailUrl = creative.thumbnailUrl || creative.url || creative.videoUrl;
                                const isVideo = creative.isVideo || !!creative.videoUrl;

                                return (
                                    <div
                                        key={creative.id}
                                        onClick={() => handleSelect(creative)}
                                        style={{
                                            position: 'relative',
                                            borderRadius: 'var(--radius-lg)',
                                            overflow: 'hidden',
                                            border: isSelected
                                                ? '3px solid var(--color-primary)'
                                                : '1px solid var(--color-border)',
                                            cursor: 'pointer',
                                            transition: 'all var(--transition-fast)',
                                            background: 'var(--color-bg-secondary)'
                                        }}
                                    >
                                        {/* Thumbnail */}
                                        <div style={{
                                            width: '100%',
                                            aspectRatio: '1',
                                            background: 'var(--color-bg-tertiary)',
                                            position: 'relative'
                                        }}>
                                            {thumbnailUrl ? (
                                                <img
                                                    src={thumbnailUrl}
                                                    alt={creative.title}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <Image size={32} style={{ color: 'var(--color-text-tertiary)' }} />
                                                </div>
                                            )}

                                            {/* Video indicator */}
                                            {isVideo && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 'var(--space-2)',
                                                    left: 'var(--space-2)',
                                                    background: 'rgba(0,0,0,0.7)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    padding: '2px 6px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-1)'
                                                }}>
                                                    <Film size={12} color="white" />
                                                    <span style={{
                                                        fontSize: '10px',
                                                        color: 'white',
                                                        fontWeight: 600
                                                    }}>
                                                        Video
                                                    </span>
                                                </div>
                                            )}

                                            {/* Selection indicator */}
                                            {isSelected && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 'var(--space-2)',
                                                    right: 'var(--space-2)',
                                                    width: '24px',
                                                    height: '24px',
                                                    borderRadius: '50%',
                                                    background: 'var(--color-primary)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <Check size={14} color="white" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div style={{ padding: 'var(--space-2) var(--space-3)' }}>
                                            <div style={{
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: 500,
                                                color: 'var(--color-text-primary)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {creative.title || 'Untitled'}
                                            </div>
                                            <div style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-tertiary)',
                                                marginTop: '2px'
                                            }}>
                                                {new Date(creative.createdAt).toLocaleDateString()}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <div style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        {filteredCreatives.length} creative{filteredCreatives.length !== 1 ? 's' : ''} available
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleConfirm}
                            disabled={!selectedCreative}
                        >
                            Select Creative
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreativePickerModal;
